import type { AxiosInstance } from 'axios';

import { createClient } from './core/client';
import { readEnvNumber } from './env';
import { getServiceUrl } from './environment';
import type { IzteachService } from './environment';
import { izteachSession, qltSession, sessionHooks } from './session';

const TIMEOUT = readEnvNumber('VITE_API_TIMEOUT', 30000);

// No auth adapter: no token is attached and a 401 never signs out. Still gets param
// stripping and ApiError normalization.
export const bareQltClient: AxiosInstance = createClient(getServiceUrl('qlt'), {
  timeout: TIMEOUT,
});

// QLT / Strapi v3: Bearer jwt; board sessions never renew, so a 401 signs out.
export const qltClient: AxiosInstance = createClient(getServiceUrl('qlt'), {
  timeout: TIMEOUT,
  auth: {
    headers: () => (qltSession.jwt ? { Authorization: `Bearer ${qltSession.jwt}` } : undefined),
    onAuthFailure: (message) => sessionHooks.onQltAuthFailure(message),
  },
});

// IZTeach gateways: RAW accessToken header (no Bearer) + vendorId on every request.
const izteachClients: Record<string, AxiosInstance> = {};

export function getIzteachClient(service: IzteachService): AxiosInstance {
  const existing = izteachClients[service];
  if (existing) return existing;
  const client = createClient(getServiceUrl(service), {
    timeout: TIMEOUT,
    auth: {
      headers: () =>
        izteachSession.accessToken ? { Authorization: izteachSession.accessToken } : undefined,
      params: () => (izteachSession.vendorId ? { vendorId: izteachSession.vendorId } : undefined),
      // Credentials are minted by the qlt-signin exchange, so recovery re-runs that exchange.
      recover: () => sessionHooks.reauthorizeIzteach(),
      onAuthFailure: (message) => sessionHooks.onIzteachAuthFailure(message),
    },
  });
  izteachClients[service] = client;
  return client;
}

// Unauthenticated gateway instance for the sign-in handshake itself.
const bareIzteachClients: Record<string, AxiosInstance> = {};

export function bareIzteachClient(service: IzteachService): AxiosInstance {
  const existing = bareIzteachClients[service];
  if (existing) return existing;
  const client = createClient(getServiceUrl(service), { timeout: TIMEOUT });
  bareIzteachClients[service] = client;
  return client;
}
